import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  getCardStatementStatus, parseCardComplaint, uploadCardStatement,
  matchCardStatement, resolveMatchedCardItems, sendCardItemsToBank,
  updateCardItemManual, updateCardItemBankResponse, deleteCardStatement,
} from '../../lib/api';

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-700 border-gray-300',
  matched: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  partial: 'bg-amber-100 text-amber-800 border-amber-300',
  not_found: 'bg-red-100 text-red-700 border-red-300',
  sent_to_bank: 'bg-blue-100 text-blue-700 border-blue-300',
  resolved: 'bg-green-100 text-green-800 border-green-400',
  bank_denied: 'bg-rose-100 text-rose-700 border-rose-300',
};

const STATUS_LABELS = {
  pending: 'Pending',
  matched: 'Matched',
  partial: 'Partial Match',
  not_found: 'Not Found',
  sent_to_bank: 'Sent to Bank',
  resolved: 'Resolved ✓',
  bank_denied: 'Bank Denied',
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

export default function CardComplaintPanel({ ticket, isAdmin, onTimelineRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [bankNote, setBankNote] = useState('');
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!ticket?.id) return;
    try {
      setLoading(true);
      const res = await getCardStatementStatus(ticket.id);
      setData(res.data);
      setSelected(new Set());
    } catch (err) {
      // 403 for non-admin non-creator — fine, hide panel
      if (err?.response?.status !== 403) {
        console.warn('Card statement fetch fail', err);
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [ticket?.id]);

  useEffect(() => { load(); }, [load]);

  const hasCardsInDesc = /\d{16}/.test(ticket?.description || '');
  const isCardVendorTicket = ticket?.created_by_role === 'card_vendor';
  const shouldShow = hasCardsInDesc || isCardVendorTicket;

  if (!shouldShow) return null;
  if (loading) {
    return (
      <div className="border rounded-lg overflow-hidden bg-blue-50/30 p-3">
        <p className="text-xs text-gray-500">Card statement match loading...</p>
      </div>
    );
  }

  const items = data?.items || [];
  const stats = data?.stats || {};
  const statements = data?.statements || [];

  const doParse = async () => {
    try {
      setBusy('parse');
      const res = await parseCardComplaint(ticket.id);
      toast.success(`${res.data.inserted} card items parse ho gaye`);
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Parse fail');
    } finally { setBusy(''); }
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Sirf PDF file allowed hai');
      return;
    }
    try {
      setBusy('upload');
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadCardStatement(ticket.id, fd);
      toast.success(`Statement uploaded — Card ****${res.data.card_last4} · ${res.data.txn_count} txns`);
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload fail');
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const doMatch = async () => {
    try {
      setBusy('match');
      const res = await matchCardStatement(ticket.id);
      const { matched = 0, partial = 0, not_found = 0 } = res.data;
      toast.success(`Match complete: ${matched} matched · ${partial} partial · ${not_found} not found`);
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Match fail');
    } finally { setBusy(''); }
  };

  const doResolveMatched = async () => {
    if (!window.confirm('Sabhi matched items resolve mark kar dun? Yeh action ledger timeline mein log hoga.')) return;
    try {
      setBusy('resolve');
      const res = await resolveMatchedCardItems(ticket.id, { resolution_notes: 'Bank statement se auto-verify ho gaya' });
      toast.success(`${res.data.resolved} items resolved`);
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resolve fail');
    } finally { setBusy(''); }
  };

  const doSendToBank = async () => {
    const note = bankNote.trim();
    if (!note) {
      toast.error('Bank note likhein (query details ke saath)');
      return;
    }
    const ids = selected.size > 0 ? [...selected] : null;
    const msg = ids
      ? `${ids.length} selected items ko bank ko bhejein?`
      : 'Sabhi unmatched (not_found + partial + pending) items ko bank ko bhejein?';
    if (!window.confirm(msg)) return;
    try {
      setBusy('bank');
      const res = await sendCardItemsToBank(ticket.id, { bank_notes: note, item_ids: ids });
      toast.success(`${res.data.sent} items bank ko forward ho gaye (₹${res.data.total_amount})`);
      setBankNote('');
      setSelected(new Set());
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send-to-bank fail');
    } finally { setBusy(''); }
  };

  const setManualStatus = async (itemId, newStatus, notes = '') => {
    try {
      await updateCardItemManual(itemId, { match_status: newStatus, notes });
      toast.success(`Status → ${STATUS_LABELS[newStatus] || newStatus}`);
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update fail');
    }
  };

  const setBankResponse = async (itemId, response) => {
    const notes = window.prompt(`Bank response (${response}) — koi note?`) || '';
    try {
      await updateCardItemBankResponse(itemId, { response, notes });
      toast.success(`Bank response saved: ${response}`);
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update fail');
    }
  };

  const doDeleteStatement = async (sid, name) => {
    if (!window.confirm(`"${name}" statement delete karein? Matched items reset ho jayenge.`)) return;
    try {
      await deleteCardStatement(sid);
      toast.success('Statement deleted');
      await load();
      onTimelineRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete fail');
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const unmatchedCount = (stats.not_found || 0) + (stats.pending || 0) + (stats.partial || 0);
  const matchableCount = (stats.matched || 0) + (stats.partial || 0);
  const totalClaimed = data?.total_claimed || 0;
  const totalMatched = data?.total_matched_amount || 0;

  return (
    <div className="border-2 border-indigo-200 rounded-lg overflow-hidden shadow-sm" data-testid="card-complaint-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg">🏦</span>
          <p className="text-white text-sm font-semibold">Card Refund Verification (Bank Statement)</p>
          {items.length > 0 && (
            <span className="bg-white/20 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
              {items.length} items · {fmt(totalClaimed)}
            </span>
          )}
        </div>
        {items.length > 0 && (
          <div className="flex gap-1 text-[10px] flex-wrap">
            {stats.resolved > 0 && <span className="bg-green-500 text-white px-1.5 py-0.5 rounded font-semibold">✓ {stats.resolved} RESOLVED</span>}
            {stats.matched > 0 && <span className="bg-emerald-400 text-emerald-900 px-1.5 py-0.5 rounded">{stats.matched} MATCHED</span>}
            {stats.partial > 0 && <span className="bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded">{stats.partial} PARTIAL</span>}
            {stats.not_found > 0 && <span className="bg-red-500 text-white px-1.5 py-0.5 rounded">{stats.not_found} NOT FOUND</span>}
            {stats.sent_to_bank > 0 && <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded">{stats.sent_to_bank} AT BANK</span>}
            {stats.pending > 0 && <span className="bg-gray-400 text-white px-1.5 py-0.5 rounded">{stats.pending} PENDING</span>}
          </div>
        )}
      </div>

      {/* Actions bar */}
      {isAdmin && (
        <div className="bg-indigo-50/60 border-b border-indigo-100 p-2 flex items-center gap-2 flex-wrap">
          <button
            data-testid="card-parse-btn"
            onClick={doParse}
            disabled={busy === 'parse'}
            className="text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium"
          >
            {busy === 'parse' ? 'Parsing...' : '1️⃣ Parse Cards from Description'}
          </button>
          <label className="text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium cursor-pointer" data-testid="card-upload-btn">
            {busy === 'upload' ? 'Uploading...' : '2️⃣ Upload Bank PDF'}
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={doUpload} disabled={busy === 'upload'} />
          </label>
          <button
            data-testid="card-match-btn"
            onClick={doMatch}
            disabled={busy === 'match' || items.length === 0 || statements.length === 0}
            className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-3 py-1.5 rounded font-medium"
          >
            {busy === 'match' ? 'Matching...' : '3️⃣ Run Auto-Match'}
          </button>
          {matchableCount > 0 && (
            <button
              data-testid="card-resolve-matched-btn"
              onClick={doResolveMatched}
              disabled={busy === 'resolve'}
              className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium"
            >
              {busy === 'resolve' ? '...' : `✓ Resolve ${matchableCount} Matched`}
            </button>
          )}
        </div>
      )}

      {/* Summary */}
      {items.length > 0 && (
        <div className="bg-white px-3 py-2 border-b flex items-center justify-between gap-4 text-xs flex-wrap">
          <div>
            <span className="text-gray-500">Claimed Total:</span>{' '}
            <strong className="text-base text-gray-900">{fmt(totalClaimed)}</strong>
          </div>
          <div>
            <span className="text-gray-500">Matched Total:</span>{' '}
            <strong className={`text-base ${totalMatched >= totalClaimed ? 'text-emerald-700' : 'text-amber-700'}`}>
              {fmt(totalMatched)}
            </strong>
          </div>
          <div>
            <span className="text-gray-500">Statements:</span>{' '}
            <strong>{statements.length}</strong>
          </div>
        </div>
      )}

      {/* Statements list */}
      {statements.length > 0 && (
        <div className="bg-slate-50 px-3 py-2 border-b space-y-1" data-testid="card-statements-list">
          {statements.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2 py-1">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-purple-600">📄</span>
                <span className="font-mono text-gray-800 truncate max-w-[200px]" title={s.filename}>{s.filename}</span>
                <span className="text-blue-700 font-semibold whitespace-nowrap">Card ****{s.card_last4 || '????'}</span>
                <span className="text-gray-500 whitespace-nowrap">{s.txn_count} txns</span>
                <span className="text-emerald-700 whitespace-nowrap">Cr {fmt(s.credit_total)}</span>
                <span className="text-gray-400 hidden sm:inline">· {fmtDate(s.uploaded_at)}</span>
              </div>
              {isAdmin && (
                <button
                  onClick={() => doDeleteStatement(s.id, s.filename)}
                  className="text-red-500 hover:text-red-700 text-xs px-1"
                  title="Delete statement"
                  data-testid={`delete-stmt-${s.id}`}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Items table */}
      {items.length > 0 ? (
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr className="text-[10px] uppercase tracking-wider text-gray-600">
                {isAdmin && <th className="px-2 py-1.5 w-6"></th>}
                <th className="px-2 py-1.5 text-left">#</th>
                <th className="px-2 py-1.5 text-left">Card Number</th>
                <th className="px-2 py-1.5 text-right">Claimed</th>
                <th className="px-2 py-1.5 text-left">Match Status</th>
                <th className="px-2 py-1.5 text-left">Matched On</th>
                <th className="px-2 py-1.5 text-left">Ref / Description</th>
                {isAdmin && <th className="px-2 py-1.5 text-left w-[140px]">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const canSelect = isAdmin && ['not_found', 'partial', 'pending'].includes(it.match_status);
                return (
                  <tr key={it.id} className="border-t hover:bg-slate-50" data-testid={`card-item-${idx}`}>
                    {isAdmin && (
                      <td className="px-2 py-1.5 text-center">
                        {canSelect ? (
                          <input
                            type="checkbox"
                            checked={selected.has(it.id)}
                            onChange={() => toggleSelect(it.id)}
                            className="cursor-pointer"
                            data-testid={`card-item-select-${idx}`}
                          />
                        ) : null}
                      </td>
                    )}
                    <td className="px-2 py-1.5 text-gray-500">{idx + 1}</td>
                    <td className="px-2 py-1.5 font-mono text-blue-900" data-testid={`card-item-cardno-${idx}`}>{it.card_number}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmt(it.claimed_amount)}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLES[it.match_status] || STATUS_STYLES.pending}`}>
                        {STATUS_LABELS[it.match_status] || it.match_status}
                        {it.match_confidence > 0 && <span className="opacity-70">({it.match_confidence}%)</span>}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{fmtDate(it.matched_date)}</td>
                    <td className="px-2 py-1.5 text-gray-500 max-w-[220px] truncate" title={it.matched_description || it.notes || ''}>
                      {it.matched_ref && <span className="font-mono text-[10px]">{it.matched_ref}</span>}
                      {it.matched_description && <span className="text-gray-600"> · {it.matched_description}</span>}
                      {!it.matched_description && it.bank_notes && <span className="text-blue-700 italic">Bank: {it.bank_notes.slice(0, 40)}</span>}
                    </td>
                    {isAdmin && (
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1 flex-wrap">
                          {['matched', 'partial'].includes(it.match_status) && (
                            <button
                              onClick={() => setManualStatus(it.id, 'resolved')}
                              className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-1.5 py-0.5 rounded"
                              data-testid={`card-item-resolve-${idx}`}
                            >
                              ✓ Resolve
                            </button>
                          )}
                          {it.match_status === 'sent_to_bank' && (
                            <>
                              <button
                                onClick={() => setBankResponse(it.id, 'refunded')}
                                className="text-[10px] bg-green-600 hover:bg-green-700 text-white px-1.5 py-0.5 rounded"
                              >Refunded</button>
                              <button
                                onClick={() => setBankResponse(it.id, 'not_refunded')}
                                className="text-[10px] bg-red-600 hover:bg-red-700 text-white px-1.5 py-0.5 rounded"
                              >Denied</button>
                            </>
                          )}
                          {['not_found', 'pending'].includes(it.match_status) && (
                            <button
                              onClick={() => setManualStatus(it.id, 'matched', 'Manually verified')}
                              className="text-[10px] bg-amber-500 hover:bg-amber-600 text-white px-1.5 py-0.5 rounded"
                              data-testid={`card-item-mark-matched-${idx}`}
                            >
                              Mark Match
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4 text-center text-sm text-gray-500 bg-white">
          {isAdmin ? (
            <>
              <p className="mb-1">Abhi tak koi card item parse nahi hua.</p>
              <p className="text-xs">Description mein 16-digit card numbers hain to <b>1️⃣ Parse Cards</b> button dabao.</p>
            </>
          ) : (
            <p>Admin ne abhi tak card statement match run nahi kiya.</p>
          )}
        </div>
      )}

      {/* Send-to-bank composer */}
      {isAdmin && unmatchedCount > 0 && (
        <div className="bg-blue-50/60 border-t border-blue-100 p-2 space-y-2" data-testid="card-bank-composer">
          <p className="text-xs font-semibold text-blue-900">
            📧 Bank ko forward karein (Internal Mail)
            {selected.size > 0 && <span className="ml-2 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">{selected.size} selected</span>}
          </p>
          <textarea
            data-testid="card-bank-note"
            value={bankNote}
            onChange={(e) => setBankNote(e.target.value)}
            placeholder="Bank ko query note (e.g. 'Ye 6 refund transactions statement me nahi mile — pls verify')..."
            rows={2}
            className="w-full text-xs border rounded p-2 focus:outline-none focus:border-blue-500"
          />
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <p className="text-[10px] text-gray-600">
              {selected.size > 0 ? `${selected.size} items selected` : `${unmatchedCount} unmatched items (sabhi bhejein)`}
            </p>
            <button
              data-testid="card-send-bank-btn"
              onClick={doSendToBank}
              disabled={busy === 'bank' || !bankNote.trim()}
              className="text-xs bg-blue-700 hover:bg-blue-800 disabled:opacity-40 text-white px-4 py-1.5 rounded font-medium"
            >
              {busy === 'bank' ? 'Sending...' : `📧 Send to Bank ${selected.size > 0 ? `(${selected.size})` : `(${unmatchedCount})`}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
